/**
 * BacktestChart.tsx
 *
 * Performance chart component showing strategy returns over time.
 * Displays strategies + benchmarks with entry restriction zones.
 */

import React, { useMemo, useState } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceArea,
  ReferenceLine
} from 'recharts';
import { BacktestResponse, getStrategyColor, formatStrategyName } from '../../types/backtest';

interface BacktestChartProps {
  results: BacktestResponse;
}

const BacktestChart: React.FC<BacktestChartProps> = ({ results }) => {
  const [hiddenLines, setHiddenLines] = useState<Set<string>>(new Set());
  const [showBlockedZones, setShowBlockedZones] = useState<boolean>(true);

  // Transform daily values from all strategies into chart data points
  const chartData = useMemo(() => {
    const dataMap = new Map<string, Record<string, any>>();

    // Get initial capital from first day of any available strategy
    const firstStrategy = Object.values(results.strategies)[0];
    const initialCapital = firstStrategy?.daily_values[0]?.total_value || 10000;

    // Process each strategy dynamically
    Object.entries(results.strategies).forEach(([strategyName, strategyResult]) => {
      strategyResult.daily_values.forEach((dv) => {
        if (!dataMap.has(dv.date)) {
          dataMap.set(dv.date, { date: dv.date });
        }
        const point = dataMap.get(dv.date)!;
        // Calculate return percentage from initial capital
        const returnPct = ((dv.total_value - initialCapital) / initialCapital) * 100;
        point[strategyName] = returnPct;
      });
    });

    // Add all benchmark data dynamically
    Object.entries(results.benchmarks).forEach(([benchmarkName, benchmarkData]) => {
      const dailyValues = benchmarkData?.daily_values ?? [];
      dailyValues.forEach(dv => {
        if (dataMap.has(dv.date)) {
          const point = dataMap.get(dv.date)!;
          point[benchmarkName] = dv.return_pct;
        }
      });
    });

    return Array.from(dataMap.values()).sort((a, b) =>
      String(a.date).localeCompare(String(b.date))
    );
  }, [results]);

  // Get blocked zones from technical data
  const blockedZones = results.technical_data?.blocked_zones ?? [];

  const toggleLine = (key: string) => {
    setHiddenLines((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(key)) {
        newSet.delete(key);
      } else {
        newSet.add(key);
      }
      return newSet;
    });
  };

  const formatXAxis = (tickItem: string): string => {
    const date = new Date(tickItem);
    return date.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
  };

  const formatYAxis = (value: number): string => {
    return `${value >= 0 ? '+' : ''}${value.toFixed(0)}%`;
  };

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      // Check if this date is in a blocked zone
      const isBlocked = results.technical_data?.daily?.find(d => d.date === label)?.blocked;

      return (
        <div className="bt-tip">
          <p className="bt-tip-d">
            {new Date(label).toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              year: 'numeric'
            })}
            {isBlocked && <b>ENTRY BLOCKED</b>}
          </p>
          {payload.map((entry: any, index: number) => (
            <div key={index} className="bt-tip-r">
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: 'var(--tk-t2)' }}>
                <span className="bt-dot" style={{ background: entry.color }} />
                {formatStrategyName(entry.name)}
              </span>
              <span className={`n ${entry.value >= 0 ? 'is-gain' : 'is-loss'}`}>
                {entry.value >= 0 ? '+' : ''}{entry.value.toFixed(2)}%
              </span>
            </div>
          ))}
        </div>
      );
    }
    return null;
  };

  // Dynamically build lines from available strategies
  const availableStrategies = Object.keys(results.strategies);
  const strategyLines = availableStrategies.map(key => ({
    key,
    name: formatStrategyName(key),
    color: getStrategyColor(key),
    isStrategy: true
  }));

  // Dynamically build benchmark lines
  const benchmarkLines = Object.keys(results.benchmarks).map(key => ({
    key,
    name: key === results.symbol ? `${key} (Buy & Hold)` : key,
    color: key === results.symbol ? '#e7e8ea' : getStrategyColor(key), // Neutral light for the stock itself
    isStrategy: false
  }));

  const lines = [...strategyLines, ...benchmarkLines];

  const tick = { fill: '#6f747c', fontSize: 10.5, fontFamily: "'JetBrains Mono Variable', 'JetBrains Mono', ui-monospace, monospace" };

  return (
    <div className="bt" style={{ minWidth: 0 }}>
      {/* Legend/Toggle */}
      <div className="bt-leg">
        {lines.map((line) => (
          <button
            key={line.key}
            type="button"
            className={`bt-leg-b${hiddenLines.has(line.key) ? ' is-off' : ''}`}
            aria-pressed={!hiddenLines.has(line.key)}
            onClick={() => toggleLine(line.key)}
          >
            <span className="bt-dot" style={{ background: line.color }} />
            {line.name}
          </button>
        ))}

        {/* Blocked zones toggle */}
        {blockedZones.length > 0 && (
          <button
            type="button"
            className={`bt-leg-b${showBlockedZones ? '' : ' is-off'}`}
            aria-pressed={showBlockedZones}
            onClick={() => setShowBlockedZones(!showBlockedZones)}
          >
            <span className="bt-zone" />
            Entry Blocked (<span className="n">{blockedZones.length}</span>)
          </button>
        )}
      </div>

      {/* Chart */}
      <div className="bt-chart">
        <ResponsiveContainer>
          <LineChart
            data={chartData}
            margin={{ top: 5, right: 12, left: 0, bottom: 5 }}
          >
            <CartesianGrid stroke="rgba(255,255,255,0.05)" vertical={false} />
            <XAxis
              dataKey="date"
              tickFormatter={formatXAxis}
              tick={tick}
              tickLine={false}
              axisLine={{ stroke: 'rgba(255,255,255,0.08)' }}
              minTickGap={24}
            />
            <YAxis
              tickFormatter={formatYAxis}
              tick={tick}
              tickLine={false}
              axisLine={false}
              width={48}
            />
            <Tooltip content={<CustomTooltip />} cursor={{ stroke: 'rgba(255,255,255,0.18)' }} />

            {/* Zero reference line */}
            <ReferenceLine y={0} stroke="rgba(255,255,255,0.18)" strokeDasharray="3 3" />

            {/* Blocked zones (entry restriction periods) */}
            {showBlockedZones && blockedZones.map((zone, index) => (
              <ReferenceArea
                key={`blocked-${index}`}
                x1={zone.start}
                x2={zone.end}
                fill="#e08a7e"
                fillOpacity={0.08}
                stroke="#e08a7e"
                strokeOpacity={0.25}
              />
            ))}

            {/* Strategy lines */}
            {lines.map((line) => (
              !hiddenLines.has(line.key) && (
                <Line
                  key={line.key}
                  type="monotone"
                  dataKey={line.key}
                  name={line.key}
                  stroke={line.color}
                  strokeWidth={line.isStrategy ? 1.75 : 1.25}
                  dot={false}
                  strokeDasharray={!line.isStrategy ? '4 4' : undefined}
                  activeDot={{ r: 3, stroke: line.color, strokeWidth: 1.5, fill: '#16171b' }}
                />
              )
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Chart Legend Note */}
      <div className="bt-chart-note">
        Click legend to show/hide. Solid = strategies, Dashed = benchmarks.
        {blockedZones.length > 0 && ' Red zones = entry filter blocked.'}
      </div>
    </div>
  );
};

export default BacktestChart;
